<template>

  <div id="container">
    <p><b>Acetaminophen psi parameter (Ψ)</b> is a parameter derived from the
      mathematical modelling of timed acetaminophen (APAP) blood
      concentration and duration between when the overdose has occurred and
      the administration of N-acetylcysteine (NAC) antidote. It has been shown to
      be a reliable predictor of hepatotoxicity from acute acetaminophen
      overdose. According to the model, both the higher serum acetaminophen
      concentration and the longer lag time between the overdose and the
      initiation of antidote will yield a larger psi parameter which is associated
      with higher risk of hepatotoxicity.</p>

    <table class="inline-table center">
      <tr>
        <th>Psi parameter
          (unit mmol/L.h)</th>
        <th>Interpretation</th>
      </tr>
      <tr>
        <td>≤0.001</td>
        <td>Minimal hepatotoxicity risk</td>
      </tr>
      <tr>
        <td>0.001 &lt; psi &lt; 5.0</td>
        <td>Possible hepatotoxicity</td>
      </tr>
      <tr>
        <td>≥5.0 </td>
        <td>High risk of hepatotoxicity</td>
      </tr>
    </table>

    <p><b>Acetaminophen-aminotransferase multiplication product</b> is another
      parameter which can clinically predict hepatotoxicity from an acute
      acetaminophen overdose. The multiplication product is derived by
      multiplying the patient's simultaneously sampled serum acetaminophen
      level and aminotransferase concentration. A larger multiplication product
      can result from a higher acetaminophen concentration and/or a higher
      aminotransferase level. The larger multiplication product is associated with
      higher risk of hepatotoxicity.</p>

    <table class="inline-table center">
      <tr>
        <th>Acetaminophen-aminotransferase<br>
          multiplication product<br>
          (unit mg units/L 2 )</th>
        <th>Interpretation</th>
      </tr>
      <tr>
        <td>≤1,500</td>
        <td>Minimal hepatotoxicity risk</td>
      </tr>
      <tr>
        <td>1,500-9,999</td>
        <td>Possible hepatotoxicity</td>
      </tr>
      <tr>
        <td>≥10,000 </td>
        <td>High risk of hepatotoxicity</td>
      </tr>
    </table>
    <p>Both the psi parameter and acetaminophen aminotransferase multiplication
      product are useful clinical tools in medical care of patients with acute
      acetaminophen overdose, because they allow healthcare professionals to
      predict the risk of hepatotoxicity and individualize the treatment to achieve
      optimal outcomes.</p>
    <h1>Instructions</h1>
    <h4>Psi parameter</h4>
    <p>To calculate psi parameter, please follow the steps (Please press the
      yellow reset bar every time before starting the calculation.)<br><br>
      Step 1: Enter the measured serum acetaminophen concentration in
      milligram per liter (mg/L). This should be sampled at least 4 hours after the
      ingestion.<br><br>
      Step 2: Enter the time lapse (hours) between when ingestion has occurred
      and the blood sampling.<br><br>
      Step 3: Enter the time lapse (hours) between when ingestion has occurred
      and the initiation of N-acetylcysteine therapy<br><br>
      Once the steps 1 and 2 are entered correctly, the calculator will generate
      the following information.
    <ol>
      <li>An estimated 4-hour acetaminophen concentration for the patient.
        This is based on a back-extrapolation method, using the gradient of
        the treatment line on the Rumack-Matthew Nomogram</li>
      <li>Interpretation of the acetaminophen concentration according to the
        150 mg/L treatment line on the Rumack-Matthew Nomogram.</li>
    </ol>
    After the steps 1, 2 and 3 are entered correctly, the calculator will generate
    the following information.
    <ol>
      <li>The patient's calculated psi parameter</li>
      <li>Interpretation of the psi parameter</li>
    </ol>
    </p>
    <h4>Acetaminophen-aminotransferase multiplication product</h4>
    <p>
      To calculate acetaminophen-aminotransferase multiplication product,
      please follow the steps. (Please press the yellow reset bar every time
      before starting the calculation.)<br><br>
      Step 1: Enter the serum acetaminophen concentration in milligram per liter
      (mg/L). This should be sampled at least 4 hours after the ingestion.<br><br>
      Step 2: Enter the aminotransferase concentration from the blood sample
      drawn at the same time as the serum acetaminophen concentration. Both
      the aspartate aminotransferase (AST) or alanine aminotransferase (ALT)
      (unit/L) should be entered. If the value is not available, enter 0.<br><br>
      Once the values are entered correctly, the calculator will generate the
      following information.
    <ol>
      <li>The acetaminophen-aminotransferase multiplication product</li>
      <li>Interpretation of the acetaminophen-aminotransferase multiplication product</li>
    </ol>
    </p>
    <h1>App Developers</h1>
    <ul>
      <li>Sunsern Cheamanunkul (sunsern.che@mahidol.edu)</li>
      <li>Pinsumon Chomchai</li>
    </ul>
    <h1>References</h1>
    <ol>
      <li>Chomchai S, Chomchai C. Predicting acute acetaminophen
        hepatotoxicity with acetaminophen-aminotransferase multiplication product
        and the Psi parameter. Clin Toxicol (Phila). 2014;52(5):506-11.</li>
      <li>Chomchai S, Chomchai C, Anusornsuwan T. Acetaminophen psi
        parameter: a useful tool to quantify hepatotoxicity risk in acute
        acetaminophen overdose. Clin Toxicol (Phila). 2011;49(7):664-7.</li>
      <li>Wong A, Graudins A. Risk prediction of hepatotoxicity in paracetamol
        poisoning. Clin Toxicol (Phila). 2017;55(8):879-92.</li>
      <li>Hendrickson RG. What is the most appropriate dose of N-acetylcysteine
        after massive acetaminophen overdose? Clin Toxicol (Phila).
        2019;57(8):686-91.
      </li>
    </ol>
    <h3>For any inquiry about this application, please contact us at <a href="mailto:acetacalc@gmail.com">acetacalc@gmail.com</a></h3>
  </div>

</template>

<script lang="ts">
export default {
  name: "Information",
};
</script>


<style scoped>
#container {
  text-align: left;
  position: absolute;
  left: 0;
  right: 0;
  margin-left: 2em;
  padding-bottom: 5em;
}

.center {
  margin-left: auto;
  margin-right: auto;
}

.inline-table table,
td,
th {
  border: 1px solid;
  border-collapse: collapse;
  padding: 5px;
}
</style>